import { permissionAllows, sidebarFilterRoutes } from './sidebarHelpers';

/**
 * Who may open which screen - ONE answer for the sidebar, the router and the dashboard's module grid.
 *
 * The sidebar already decides what a user may see (module switch, per-child action, `anyOf`). The router used to
 * decide again from the route table with a different rule, so the two could disagree: a link shown but refused
 * (the user is bounced back to the dashboard), or a screen with no guard at all that anyone signed in could open
 * by typing its URL. These helpers make the sidebar the single source: whatever it shows, the router allows, and
 * the dashboard grid is built from the same filter.
 */

const normPath = (p) => (String(p || '').replace(/\/+$/, '') || '/');

/**
 * `path -> [{ entry, parentModuleId }]` for every sidebar item and child, so the router can ask "what does the
 * sidebar require for this path?". A path may appear twice (a group and its first child) - any of them allowing it
 * is enough, exactly as the sidebar shows the link when any entry is visible.
 */
export const buildPathGuards = (sidebarRoutes = []) => {
	const guards = new Map();
	const add = (entry, parentModuleId) => {
		if (!entry?.path || entry.path === '/default') return;
		const key = normPath(entry.path);
		if (!guards.has(key)) guards.set(key, []);
		guards.get(key).push({ entry, parentModuleId });
	};
	const walk = (route, parentModuleId) => {
		add(route, parentModuleId);
		(route.children || []).forEach((child) => walk(child, route.moduleId ?? parentModuleId));
	};
	sidebarRoutes.forEach((item) => (item.routes || [item]).forEach((route) => walk(route)));
	return guards;
};

const explicitGuard = (hasPermission, route) => {
	if (route.anyOf) return permissionAllows(hasPermission, route);
	if (route.parent && route.childId) return hasPermission(route.parent, route.childId);
	if (route.moduleId) return hasPermission(route.moduleId);
	return null; // this route declares no guard of its own
};

const sidebarGuard = (hasPermission, route, guards) => {
	const entries = guards.get(normPath(route.path));
	if (!entries?.length) return null;
	return entries.some(({ entry, parentModuleId }) => permissionAllows(hasPermission, entry, parentModuleId));
};

const ownerCache = new WeakMap();

/**
 * The sidebar entries of the NEAREST sidebar page above this path (`/admin/dev-intel/projects/:id` -> the Projects
 * entry), but only when that page is gated by a specific ACTION (an `id` or `anyOf`). A module-level owner adds
 * nothing a module-level route guard does not already say, so it returns null.
 */
const ownerEntries = (guards, path) => {
	let cache = ownerCache.get(guards);
	if (!cache) ownerCache.set(guards, (cache = new Map()));
	const key = normPath(path);
	if (cache.has(key)) return cache.get(key);
	const segments = key.split('/').filter(Boolean);
	let found = null;
	for (let n = segments.length - 1; n > 0 && !found; n -= 1) {
		const entries = guards.get('/' + segments.slice(0, n).join('/'));
		if (entries?.length) found = entries.some(({ entry }) => entry.id || entry.anyOf) ? entries : [];
	}
	const result = found && found.length ? found : null;
	cache.set(key, result);
	return result;
};

/**
 * May the router render this route entry?
 *   1. a page the sidebar lists: the SIDEBAR's rule decides - the same one that shows or hides the link, so a link
 *      shown always opens and a page whose action is not granted does not open by URL even when the route itself
 *      only says "module" (All listings, Close deals, Create announcement...). A route that names a stricter action
 *      of its own (`anyOf`, `parent`+`childId`) must also pass that;
 *   2. a route the sidebar does not list: its own guard (`anyOf`, `parent`+`childId`, `moduleId`), unchanged;
 *   3. a route with neither falls back to its `parent` module, instead of being open to everyone signed in;
 *   4. a SUB-PAGE of a sidebar page that is gated by its own action (edit / detail / add screens) must also hold that
 *      action - holding the module is not enough to open "All listings > update" when only "My listings" is granted.
 *      This only tightens routes whose own guard is module-wide or missing-with-a-parent; a route that declares an
 *      action of its own, or no guard and no parent at all (profile / detail pages linked from several screens),
 *      is left exactly as it was.
 * A route with no guard and no sidebar entry and no parent stays open.
 */
export const routeAllowed = (hasPermission, route, guards) => {
	const own = explicitGuard(hasPermission, route);
	const side = guards ? sidebarGuard(hasPermission, route, guards) : null;
	if (own === null && side === null) {
		if (!route.parent) return true;
		if (!hasPermission(route.parent)) return false;
		return subPageAllowed(hasPermission, route, guards);
	}
	if (side !== null) {
		if (side !== true) return false;
		// the route may name a stricter action than the sidebar entry
		return own === null || !(route.anyOf || route.childId) || own === true;
	}
	if (own !== true) return false;
	// a route that already names its own action: nothing to add
	if (route.anyOf || route.childId) return true;
	return subPageAllowed(hasPermission, route, guards);
};

function subPageAllowed(hasPermission, route, guards) {
	const owners = guards ? ownerEntries(guards, route.path) : null;
	if (!owners) return true;
	return owners.some(({ entry, parentModuleId }) => permissionAllows(hasPermission, entry, parentModuleId));
}

/**
 * The dashboard's module grid, grouped by the sidebar's own categories.
 *
 * Built from `sidebarFilterRoutes` - the very filter the sidebar uses - so a card exists exactly when the sidebar
 * would show that module. Each card opens its FIRST ALLOWED screen, not the module's own path (a group such as
 * Payroll or WhatsApp has no screen of its own at that path, and a module's first child may be one the user lacks).
 *
 * @returns {{ category: string, modules: { key, moduleId, name, icon, path, links: {name, path}[] }[] }[]}
 */
export const buildQuickAccess = (sidebarRoutes = [], hasPermission) => {
	const groups = [];
	for (const item of sidebarRoutes) {
		if (!item.category || !item.routes?.length) continue; // the standalone Dashboard entry is where we already are
		const visible = sidebarFilterRoutes([...item.routes], hasPermission);
		const modules = [];
		for (const route of visible) {
			// The filter returns `children: []` when every child is hidden, so "is this a group?" must be read from the
			// original entry - otherwise an emptied group would fall back to its own path, which is not a screen.
			const original = item.routes.find((r) => r.path === route.path && r.moduleId === route.moduleId);
			const isGroup = (original?.children?.length || 0) > 0;
			const shown = (route.children || []).filter((c) => c.path);
			const links = isGroup ? shown.map((c) => ({ name: c.name, path: c.path })) : [{ name: route.name, path: route.path }];
			// A group whose children are all hidden has no first screen: its own path is not one.
			if (!links[0]?.path) continue;
			const hadChildren = isGroup;
			modules.push({
				key: `${route.moduleId || route.name}:${route.path}`,
				moduleId: route.moduleId,
				name: route.name,
				icon: route.icon,
				path: links[0].path,
				links: hadChildren ? links : [],
			});
		}
		if (modules.length) groups.push({ category: item.category, modules });
	}
	return groups;
};

/** Case-insensitive filter over module names and their screens; empty groups drop out. */
export const filterQuickAccess = (groups, term) => {
	const q = String(term || '').trim().toLowerCase();
	if (!q) return groups;
	return groups
		.map((g) => ({
			...g,
			modules: g.modules.filter(
				(m) => m.name.toLowerCase().includes(q) || m.links.some((l) => l.name.toLowerCase().includes(q)),
			),
		}))
		.filter((g) => g.modules.length);
};

export const countModules = (groups) => groups.reduce((n, g) => n + g.modules.length, 0);
